import { HandCoins, Plus } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { ServiceFormDialog } from '@/pages/admin/services/ServiceFormDialog';
import { useServiceTypes } from '@/hooks/catalog-admin';
import { formatCents } from '@shared/money';
import { ACTIVITY_FILTER_OPTIONS, DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';
import type { ActivityFilter } from '@shared/schemas/pagination';
import { PRICING_UNIT_LABELS, type ServiceTypeView } from '@shared/schemas/service-type';

/*
 * /admin/services — the rate card. ROADMAP.md §3.3, business-rules.md §7.
 *
 * One table doing three jobs: it classifies a job, it fills the client's request
 * dropdown, and it is the catalog the invoice builder picks from. That is why it
 * lives under FINANCE — pricing is why anyone opens it.
 *
 * **Nothing here renders a slug.** `name` is the display value and `slug` never
 * reaches the screen, which is the structural half of P5: there is no code path
 * that could put `deep_cleaning` in front of a person.
 */

export function ServicesPage() {
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<ActivityFilter>('active');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ServiceTypeView | null>(null);

  const services = useServiceTypes({ page, search, activity });

  const columns: Column<ServiceTypeView>[] = [
    {
      key: 'name',
      header: 'Service',
      hideLabelOnCard: true,
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-body font-medium text-ink-900">{row.name}</span>
          {row.description ? (
            <span className="mt-0.5 block text-meta text-ink-500">{row.description}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      render: (row) => <PriceCell service={row} />,
    },
    {
      key: 'duration',
      header: 'Default length',
      render: (row) => (
        <span className="text-body">{formatDuration(row.defaultDurationMinutes)}</span>
      ),
    },
    {
      key: 'appears',
      header: 'Appears on',
      render: (row) => (
        <div className="flex flex-wrap justify-end gap-1.5 md:justify-start">
          {/*
            Two flags, shown separately, because they genuinely differ: a trip
            fee is billable and never requested, and "Other / Custom" is
            requested and has no price.
          */}
          {row.isRequestable ? <StatusBadge label="Request form" tone="scheduled" /> : null}
          {row.isBillable ? <StatusBadge label="Invoices" tone="completed" /> : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.isActive ? (
          <span className="text-meta text-ink-500">Active</span>
        ) : (
          <StatusBadge label="Retired" tone="cancelled" />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setEditing(row);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <AdminPage
      title="Services"
      description="What Honeycomb offers, what each one costs, and where it shows up."
      actions={
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          Add service
        </Button>
      }
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label="Search services"
          placeholder="Search services"
          className="sm:max-w-xs"
        />
        <FilterPills
          label="Filter services by status"
          options={ACTIVITY_FILTER_OPTIONS}
          value={activity}
          onChange={(next) => {
            setActivity(next);
            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption="Services, their prices and where each one appears"
        columns={columns}
        rows={services.data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={services.isPending}
        error={services.error}
        onRetry={() => {
          void services.refetch();
        }}
        empty={
          <EmptyState
            icon={HandCoins}
            title={search ? 'No services match that search.' : 'No services yet.'}
            hint={
              search
                ? 'Try a shorter search, or clear it to see them all.'
                : 'A service is what you bill for and what a client picks when they request a clean.'
            }
            action={
              search ? null : (
                <Button
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  <Plus aria-hidden="true" />
                  Add service
                </Button>
              )
            }
          />
        }
      />

      {services.data ? (
        <Pagination
          page={services.data.page}
          pageSize={services.data.pageSize || DEFAULT_PAGE_SIZE}
          total={services.data.total}
          onPageChange={setPage}
          itemNoun="services"
        />
      ) : null}

      <ServiceFormDialog open={creating} onOpenChange={setCreating} />
      <ServiceFormDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        {...(editing ? { service: editing } : {})}
      />
    </AdminPage>
  );
}

function PriceCell({ service }: { service: ServiceTypeView }) {
  /*
   * Null is not zero, and this cell is where that shows.
   *
   * "Not priced" is a real answer — "Other / Custom" is quoted per job — and a
   * price of $0.00 is a different real answer, a service included at no charge.
   * Rendering a dash for both would be P1 twice over: a dash for a number that
   * is genuinely zero, and a zero for a number nobody has set.
   */
  if (service.defaultPriceCents === null) {
    return <span className="text-body italic text-ink-500">Not priced</span>;
  }

  return (
    <span className="whitespace-nowrap text-body text-ink-900">
      {formatCents(service.defaultPriceCents)}
      {service.pricingUnit === 'hourly' ? (
        <span className="text-meta text-ink-500"> / hour</span>
      ) : null}
      <span className="sr-only"> — {PRICING_UNIT_LABELS[service.pricingUnit]}</span>
    </span>
  );
}

/** `150` → `2h 30m`. Minutes alone stop being readable somewhere around 90. */
function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours === 0) return `${String(rest)}m`;
  if (rest === 0) return `${String(hours)}h`;
  return `${String(hours)}h ${String(rest)}m`;
}
